import { useEffect, useState } from "react";
import api from "@/lib/api";
import Pagination from "./Pagination";
import EmptyState from "./EmptyState";
import { Calendar } from "lucide-react";

/**
 * Vertical activity timeline. Reverse-chronological, server-paginated,
 * filterable by kind family + date range. Reusable for future entity timelines.
 *
 * endpoint  GET URL that returns { items, total, page, page_size }
 */

const KIND_ICON = {
  "account.registered":               "✨",
  "account.login":                    "🔐",
  "account.password_reset_requested": "🔁",
  "account.password_reset_completed": "🔒",
  "project.created":                  "📷",
  "render.created":                   "🎨",
  "scheme.saved":                     "🎭",
  "payment.paid":                     "💳",
  "download.selection_report":        "📄",
  "download.presentation_pdf":        "📄",
  "download.hd_jpg":                  "🖼",
  "download.share_png":               "🖼",
  "support.ticket_created":           "✉️",
  "admin.note_added":                 "📝",
  "admin.password_reset_sent":        "🔁",
  "admin.project_unlocked":           "\u{1F513}",
  "admin.disabled":                   "🚫",
  "admin.enabled":                    "✅",
  "admin.soft_deleted":               "🗑",
  "admin.restored":                   "↩️",
};

const FAMILIES = ["all", "account", "project", "render", "scheme", "payment", "download", "support", "admin"];
const FAMILY_LABEL = {
  all: "All", account: "Account", project: "Projects", render: "Renders",
  scheme: "Schemes", payment: "Payments", download: "Downloads", support: "Support",
  admin: "Admin actions",
};

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-AU");
}

export default function Timeline({ endpoint, testId = "timeline", refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [family, setFamily] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (family !== "all") params.set("kind_family", family);
    api.get(`${endpoint}?${params.toString()}`)
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  }, [endpoint, page, pageSize, family, refreshKey]);

  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {FAMILIES.map((f) => (
          <button
            key={f}
            data-testid={`${testId}-filter-${f}`}
            onClick={() => { setFamily(f); setPage(1); }}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${family === f ? "bg-brand-navy text-white border-brand-teal" : "bg-white text-brand-muted border-brand-linen hover:bg-brand-linen/30"}`}
          >
            {FAMILY_LABEL[f]}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-brand-muted py-4">Loading…</div>}

      {!loading && items.length === 0 && (
        <EmptyState
          icon={Calendar}
          title="No activity yet"
          caption="Events appear here as the customer interacts with ColourMatch."
          testId={`${testId}-empty`}
        />
      )}

      {!loading && items.length > 0 && (
        <ol className="relative border-l border-brand-linen pl-6 ml-2 space-y-6">
          {items.map((e) => (
            <li key={e.id} data-testid={`${testId}-event-${e.id}`} className="relative">
              <span className="absolute -left-[33px] top-1 w-3 h-3 bg-brand-navy" />
              <div className="text-xs text-brand-muted">
                {relTime(e.ts)} · <span className="font-mono">{e.kind}</span>
              </div>
              <div className="text-sm text-brand-ink mt-0.5">
                <span className="mr-1">{KIND_ICON[e.kind] || "•"}</span>
                {e.summary}
              </div>
              {e.meta && Object.keys(e.meta).length > 0 && (
                <button
                  data-testid={`${testId}-event-toggle-${e.id}`}
                  onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                  className="text-xs text-brand-teal hover:underline mt-1"
                >
                  {expanded === e.id ? "Hide meta" : "Show meta"}
                </button>
              )}
              {expanded === e.id && (
                <pre data-testid={`${testId}-event-meta-${e.id}`} className="mt-2 text-xs bg-brand-linen/30 p-3 overflow-x-auto whitespace-pre-wrap">
{JSON.stringify(e.meta, null, 2)}
                </pre>
              )}
            </li>
          ))}
        </ol>
      )}

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
      />
    </div>
  );
}
